import { Collections } from '@material-ui/icons';
import { Stack } from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { lazy, Suspense, useMemo, useState } from 'react';
import { InspectionPhotoDTO, VideoDTO } from '../../../loads/data/LoadDTO';
import { formatDuration } from 'shared/utils/MediaUtils';
import { useFlag } from 'shared/settings/FeatureToggles';
import { PlayIcon } from 'shared/icons/PlayIcon';
import { ErrorIcon } from 'shared/icons/ErrorIcon';
import { PhotoViewMode } from 'shared/ui/PhotoViewMode';
import {
  MorePhotosBadge,
  NonVehiclePhotoBadge,
  PhotoGrid,
  PhotoGridContainer,
  PhotoGridItem,
  VideoContainer,
  VideoDuration,
  VideoWrapper,
} from './ViewLoadInspectionPhotogridStyledComponents';
import { InspectionPhotoPendingPlaceholder } from './InspectionPhotoPendingPlaceholder';
import { isInspectionPhotoSynced } from './inspectionPhotoSync';
import {
  getRenderPhotos,
  buildFileUrls,
  buildGridMedia,
  buildMediaSlides,
} from './ViewLoadInspectionPhotoGridUtils';
import { ViewLoadInspectionDownloadButton } from './ViewLoadInspectionDownloadButton';

const PhotoGallery = lazy(() => import('shared/ui/PhotoGallery'));

interface PhotoGridProps {
  make: string;
  model: string;
  photos: InspectionPhotoDTO[];
  videos: VideoDTO[];
  type: 'pickup' | 'delivery';
  direction: 'horizontal' | 'vertical';
}

interface PhotoGridItemProps {
  photo: InspectionPhotoDTO & {
    is_video?: boolean;
    duration_in_seconds?: number;
  };
  index: number;
  total: number;
  onOpen: () => void;
  isPhotoSyncEnabled: boolean;
}

function InspectionPhotoGridItem({
  photo,
  index,
  total,
  onOpen,
  isPhotoSyncEnabled,
}: PhotoGridItemProps) {
  const isLarge = index === 0;
  const isLastWithOverlay = total > 7 && index === 6;
  const photoUrl = photo.rendered_photo_thumbnail || photo.rendered_photo || '';
  const isSynced = isInspectionPhotoSynced({
    url: photo.rendered_photo || '',
    thumbnailUrl: photo.rendered_photo_thumbnail || '',
  });
  const showSyncedThumbnail = !isPhotoSyncEnabled || isSynced;

  return (
    <PhotoGridItem
      $isLarge={isLarge}
      role="button"
      aria-label="Open inspection media"
      onClick={onOpen}
    >
      <PhotoGridContainer>
        {showSyncedThumbnail ? (
          <img src={photoUrl} alt="Inspection photo" />
        ) : (
          <InspectionPhotoPendingPlaceholder />
        )}

        {photo.is_video && (
          <>
            <VideoContainer>
              <VideoWrapper>
                <PlayIcon />
              </VideoWrapper>
            </VideoContainer>

            {photo.duration_in_seconds && (
              <VideoDuration>
                {formatDuration(photo.duration_in_seconds)}
              </VideoDuration>
            )}
          </>
        )}

        {photo.is_vehicle_detected === false && !isLastWithOverlay && (
          <NonVehiclePhotoBadge>
            <ErrorIcon />
          </NonVehiclePhotoBadge>
        )}

        {isLastWithOverlay && (
          <MorePhotosBadge>
            <TextBox variant="body-semibold" color="inherit">
              +{total - 7}
            </TextBox>
            <Collections color="inherit" />
          </MorePhotosBadge>
        )}
      </PhotoGridContainer>
    </PhotoGridItem>
  );
}

export function ViewLoadInspectionAdditionalMedia({
  make,
  model,
  photos,
  videos,
  type,
  direction,
}: PhotoGridProps) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(-1);
  // The slide the grid view was opened from; closing the grid reopens the
  // gallery there.
  const [photoGridSlideIndex, setPhotoGridSlideIndex] = useState(-1);
  const isPhotoSyncEnabled = useFlag('ctms_photo_sync');

  const total = photos.length + videos.length;

  const photosToRender = useMemo(
    () => getRenderPhotos(photos, videos, type),
    [photos, videos, type],
  );

  const fileUrls = useMemo(
    () => buildFileUrls(photos, videos, isPhotoSyncEnabled),
    [photos, videos, isPhotoSyncEnabled],
  );

  const mediaSlides = useMemo(
    () => buildMediaSlides(photos, videos, type, isPhotoSyncEnabled),
    [photos, videos, type, isPhotoSyncEnabled],
  );

  const gridMedia = useMemo(
    () => buildGridMedia(photos, videos, type, isPhotoSyncEnabled),
    [photos, videos, type, isPhotoSyncEnabled],
  );

  const fileName = `${make || 'unknown make'} ${model || 'unknown model'}`;

  return (
    <Stack space="small">
      <Stack space="xsmall">
        <TextBox variant="heading-5">Photos & Video</TextBox>
        <PhotoGrid data-direction={direction}>
          {photosToRender.map((photo, index) => (
            <InspectionPhotoGridItem
              key={photo.guid}
              photo={photo}
              index={index}
              total={total}
              isPhotoSyncEnabled={isPhotoSyncEnabled}
              onOpen={() => {
                setCurrentSlideIndex(index);
              }}
            />
          ))}
        </PhotoGrid>
      </Stack>

      <ViewLoadInspectionDownloadButton
        fileUrls={fileUrls}
        fileName={fileName}
        type={type}
      />

      <Suspense fallback={null}>
        <PhotoGallery
          slides={mediaSlides}
          currentSlideIndex={currentSlideIndex}
          setCurrentSlideIndex={setCurrentSlideIndex}
          onClose={() => {
            setCurrentSlideIndex(-1);
          }}
          onModeChange={() => {
            setPhotoGridSlideIndex(currentSlideIndex);
          }}
        />
      </Suspense>

      <PhotoViewMode
        open={photoGridSlideIndex >= 0}
        inlinePhotos={gridMedia}
        onClose={() => {
          setCurrentSlideIndex(photoGridSlideIndex);
          setPhotoGridSlideIndex(-1);
        }}
      />
    </Stack>
  );
}
